/*
 *  Copyright (c) 2022-2023 Nordcloud Oy or its affiliates. All Rights Reserved.
 */

import { useMemo } from "react";
import { scaleBand, scaleLinear } from "@visx/scale";
import dayjs from "dayjs";
import { EstatePeriod } from "~/components/DateRangeSelector";
import { isEmpty } from "~/tools";
import { calculateMaxDomain, calculateMinDomain, getMinMax } from "../../utils";
import { formatChartDates } from "../components/utils";
import {
  getAllDayValues,
  getTickDateFormatStackBarChart,
  getTicksNumStackBarChart,
  getXDatesStackBarChart,
  isValueLessThanCent,
  mapDataWithOtherCosts,
} from "../helpers";
import {
  ChartType,
  Granularity,
  Period,
  TopGroupsCostChartProps,
} from "../types";
import { getTotalValues } from "../utils";

type Props = {
  startDate: string;
  endDate: string;
  granularity: Granularity;
  data: TopGroupsCostChartProps[];
  showOthers: boolean;
  period: EstatePeriod | Period;
  keys: string[];
  width: number;
  height: number;
  chartType?: ChartType;
};

type Item = {
  [key: string]: string;
};

export function useStackBarChart({
  startDate,
  endDate,
  granularity,
  data,
  showOthers,
  chartType,
  period,
  keys,
  width,
  height,
}: Props) {
  const verticalMargin = 50;

  const xMax = width - verticalMargin;
  const yMax = height - verticalMargin;

  // Calculate number of ticks on X axis
  const endDateDayjs = dayjs(endDate).endOf(granularity);
  const xTicks = Math.ceil(endDateDayjs.diff(startDate, granularity, true));

  // Prepare array with all dates in range
  const xDates = useMemo(
    () => getXDatesStackBarChart(xTicks, startDate, granularity),
    [startDate, granularity, xTicks]
  );

  const getDate = (item: Item) => xDates.indexOf(item.date);

  const barChartData = mapDataWithOtherCosts(
    data,
    showOthers,
    chartType
  ).filter((item) => xDates.includes(item.date));

  const totalData = getTotalValues(barChartData);

  // Prepare array with all values in range
  const yData = useMemo(
    () => barChartData?.flatMap((item) => getAllDayValues(item)),
    [barChartData]
  );

  const isCostsBelowCent = data.every(({ value }) =>
    isValueLessThanCent(value)
  );

  const hasNoData = useMemo(
    () =>
      isEmpty(data ?? []) ||
      data?.every((timePoint) => Number(timePoint.value) === 0) ||
      isCostsBelowCent,
    [data, isCostsBelowCent]
  );

  const hasNegativeValues = useMemo(
    () => yData?.some((value) => Number(value) < 0),
    [yData]
  );

  const dateRangeDates =
    period === Period.RANGE && granularity === Granularity.MONTHS
      ? formatChartDates(startDate, endDate, period)
      : undefined;

  const minMax = getMinMax([...totalData, ...yData]);

  const chartData = useMemo(
    () =>
      barChartData?.map((item) => {
        const allKeys = Object.keys(item);
        const diff = arrayDiff(keys, allKeys);

        const rest = Object.fromEntries(diff.map((items) => [items, 0]));

        return {
          ...item,
          ...rest,
        };
      }) ?? [],
    [barChartData, keys]
  );

  const tickDateFormat = getTickDateFormatStackBarChart(granularity);

  const ticks = useMemo(
    () => getTicksNumStackBarChart(width, period),
    [width, period]
  );

  const xScale = useMemo(
    () =>
      scaleBand<number>({
        range: [1, xMax],
        domain: xDates?.map((_, index) => index),
        padding: 0.1,
      }),
    [xMax, xDates]
  );

  const yScale = useMemo(
    () =>
      scaleLinear<number>({
        range: [yMax, 0],
        domain: [
          calculateMinDomain(minMax, hasNoData),
          calculateMaxDomain(minMax, hasNoData),
        ],
        round: true,
        nice: true,
      }),
    [yMax, hasNoData, minMax]
  );

  return {
    xDates,
    getDate,
    hasNegativeValues,
    dateRangeDates,
    chartData,
    tickDateFormat,
    ticks,
    verticalMargin,
    xMax,
    yMax,
    xScale,
    yScale,
  };
}

function arrayDiff(arr1: string[], arr2: string[]) {
  return arr1.filter((x) => !arr2.includes(x));
}
